import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider, notification, Row, Col, Dropdown } from 'antd';
import {
  faShoppingCart,
  faDesktop,
  faChartLine,
  faBell,
  faCog,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SettingOutlined, UserOutlined, BookOutlined } from '@ant-design/icons';
import { AppDispatch, RootState } from '../../../redux/store';
import {
  fetchMerchantData,
  fetchStores,
  fetchOrderStatuses,
  fetchSalesData,
  fetchEarningsData,
} from '../../../redux/MerchantDasboard';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import {
  Container,
  HeaderRow,
  TitleSection,
  MainTitle,
  Subtitle,
  StatsGrid,
  SuggestionsGrid,
  RecommendationsSection,
  RecommendationsHeader,
  HeaderIconsContainer,
  HeaderIconButton,
  TitleWithIcons,
} from './Styles';
import Greeting from './Greeting';
import StatCard from './StatCard';
import SuggestionCard from './SuggestionCard';
import Sidebar from './Sidebar';
import OrderStatusOverview from './OrderStatusOverview';
import DevicesOverview from './DevicesOverview';
import SessionsOverview from './SessionsOverview';
import { suggestions, createSidebarButtons } from './Constant';

const MerchantDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const {
    merchantData,
    sales,
    orders,
    orderStatuses,
    earningsData,
    storeId,
    lastUpdated,
    loading,
  } = useSelector((state: RootState) => state.merchantDashboard);

  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  const earningsFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchMerchantData());
      dispatch(fetchStores());
      dispatch(fetchOrderStatuses());
      dispatch(fetchSalesData());
    }
  }, [dispatch]);

  useEffect(() => {
    const earningsKey = `${merchantData?.id}-${storeId}`;
    if (merchantData?.id && storeId && earningsFetchedRef.current !== earningsKey) {
      earningsFetchedRef.current = earningsKey;
      dispatch(fetchEarningsData({ merchantId: merchantData.id, storeId }));
    }
  }, [merchantData?.id, storeId, dispatch]);


  const showComingSoon = (feature: string) => {
    notification.info({
      message: `${feature} Coming Soon!`,
      description: 'This feature is currently under development and will be available soon.',
      duration: 4000,
      placement: 'topRight',
    });
  };

  const sidebarButtons = createSidebarButtons(showComingSoon);

  const user = {
    itemsWithoutPhotos: 10,
  };

  const deviceCount = merchantData?.devices?.length || 0;
  const sessionCount = merchantData?.currentSessions?.length || 0;

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderRow>
            <TitleSection>
              <TitleWithIcons>
                <MainTitle>Merchant Dashboard</MainTitle>
              </TitleWithIcons>
              <Subtitle>Manage your store and track performance</Subtitle>
            </TitleSection>
            <HeaderIconsContainer>
              <HeaderIconButton
                type="text"
                icon={<FontAwesomeIcon icon={faBell} />}
                onClick={() => showComingSoon('Notifications')}
              />
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'settings',
                      label: 'Settings',
                      icon: <SettingOutlined />,
                      onClick: () => navigate('/settings'),
                    },
                    {
                      key: 'profile',
                      label: 'Profile',
                      icon: <UserOutlined />,
                      onClick: () => navigate('/profile'),
                    },
                    {
                      type: 'divider',
                    },
                    {
                      key: 'help',
                      label: 'Help & Support',
                      icon: <BookOutlined />,
                      onClick: () => navigate('/help'),
                    },
                  ],
                }}
                trigger={['click']}
              >
                <HeaderIconButton
                  type="text"
                  icon={<FontAwesomeIcon icon={faCog} />}
                />
              </Dropdown>
            </HeaderIconsContainer>
          </HeaderRow>

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={18}>
              <Greeting name={merchantData?.name || ''} lastUpdated={lastUpdated} />

              <StatsGrid>
                <StatCard
                  title="Total Orders"
                  value={orders || 0}
                  icon={faShoppingCart}
                  color="blue"
                  onClick={() => setSelectedCard(selectedCard === 'orders' ? null : 'orders')}
                  isSelected={selectedCard === 'orders'}
                  trend={12}
                  subtitle="This week"
                  progress={Math.min((orders || 0) / 50 * 100, 100)}
                  loading={loading.orders}
                />
                <StatCard
                  title="Active Devices"
                  value={deviceCount}
                  icon={faDesktop}
                  color="green"
                  onClick={() => setSelectedCard(selectedCard === 'devices' ? null : 'devices')}
                  isSelected={selectedCard === 'devices'}
                  trend={5}
                  subtitle="Connected now"
                  progress={Math.min(deviceCount / 10 * 100, 100)}
                  loading={loading.merchant}
                />
                <StatCard
                  title="Current Sessions"
                  value={sessionCount}
                  icon={faChartLine}
                  color="purple"
                  onClick={() => setSelectedCard(selectedCard === 'sessions' ? null : 'sessions')}
                  isSelected={selectedCard === 'sessions'}
                  trend={-2}
                  subtitle="Active users"
                  progress={Math.min(sessionCount / 20 * 100, 100)}
                  loading={loading.merchant}
                />
                <StatCard
                  title="Total Sales"
                  value={`$${sales || 0}`}
                  icon={faChartLine}
                  color="orange"
                  onClick={() => {}}
                  isSelected={false}
                  trend={18}
                  subtitle="This month"
                  progress={75}
                  loading={loading.sales}
                />
                <StatCard
                  title="Today's Earnings"
                  value={earningsData ? `$${earningsData.summary?.netEarnings?.toFixed(2) || '0.00'}` : '$0.00'}
                  icon={faChartLine}
                  color="teal"
                  onClick={() => navigate('/merchant/earnings')}
                  isSelected={false}
                  trend={earningsData ? ((earningsData.summary?.netEarnings || 0) > 0 ? 1 : 0) : 0}
                  subtitle="Net after fees"
                  progress={earningsData ? parseFloat(Math.min((earningsData.summary?.netEarnings || 0) / 1000 * 100, 100).toFixed(2)) : 0}
                  loading={loading.earnings}
                />
              </StatsGrid>

              {selectedCard === 'orders' && (
                <OrderStatusOverview
                  orderStatuses={orderStatuses || { completed: 0, pending: 0, ready: 0 }}
                  onClose={() => setSelectedCard(null)}
                  isDarkMode={isDarkMode}
                />
              )}
              {selectedCard === 'devices' && (
                <DevicesOverview
                  deviceCount={deviceCount}
                  onClose={() => setSelectedCard(null)}
                  onAssignDevice={() => navigate('/deviceassignment')}
                />
              )}
              {selectedCard === 'sessions' && (
                <SessionsOverview
                  sessionCount={sessionCount}
                  onClose={() => setSelectedCard(null)}
                />
              )}

              <RecommendationsSection>
                <RecommendationsHeader>
                  <MainTitle>Smart Recommendations</MainTitle>
                  <Subtitle>Boost your business with these AI-powered suggestions</Subtitle>
                </RecommendationsHeader>
                <SuggestionsGrid>
                  {suggestions?.map((suggestion) => (
                    <SuggestionCard key={suggestion.id} suggestion={suggestion} user={user} />
                  ))}
                </SuggestionsGrid>
              </RecommendationsSection>
            </Col>

            <Col xs={24} lg={6}>
              <Sidebar buttons={sidebarButtons} />
            </Col>
          </Row>
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default MerchantDashboard;